import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";

const money = (v) =>
  Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const sum = (items, approvedOnly = false) =>
  (items || [])
    .filter(
      (x) => !approvedOnly || String(x.workflow_status || "APPROVED").toUpperCase() === "APPROVED"
    )
    .reduce((a, x) => a + Number(x.fee_amount || 0), 0);
const mapBook = (row) => ({
  ...row,
  id: String(row.fee_book_id),
  fee_head_name: row.feeHead?.fee_head_name || "",
  balance: Math.max(
    Number(row.fee_amount || 0) -
      sum(row.feeReceipts) -
      sum(row.feeConcessions, true) +
      sum(row.feeRefunds, true),
    0
  ),
});

export default function FeeApproval() {
  const base = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const { client_id: clientId, college_id: collegeId, user_email: userEmail = "" } = user;
  const [rollNo, setRollNo] = useState("");
  const [student, setStudent] = useState(null);
  const [rows, setRows] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ payable_date: "", fine_amount: "0", remarks: "" });
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState({ open: false, message: "", severity: "success" });
  const message = (text, severity = "success") =>
    setNotice({ open: true, message: text, severity });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const search = async () => {
    if (!rollNo.trim()) return message("Enter Student Roll No.", "warning");
    setBusy(true);
    try {
      const studentRes = await axios.get(
        `${base}/api/students/rollno/${clientId}/${collegeId}/${encodeURIComponent(rollNo.trim())}`
      );
      const found = studentRes.data?.student || studentRes.data?.data;
      if (!found?.student_id) throw new Error("Student Details are not found");
      const [bookRes, approvalRes] = await Promise.all([
        axios.get(`${base}/api/fee-books/${clientId}/${collegeId}/student/${found.student_id}`, {
          params: { workflowStatus: "ALL" },
        }),
        axios.get(`${base}/api/fee-approvals/${clientId}/${collegeId}`),
      ]);
      setStudent(found);
      setRows((bookRes.data?.data || []).map(mapBook));
      setRequests(
        (approvalRes.data?.data || []).filter(
          (x) => String(x.student_id) === String(found.student_id)
        )
      );
    } catch (e) {
      setStudent(null);
      setRows([]);
      message(e.response?.data?.message || e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!form.payable_date) return message("Select payable date", "warning");
    if (!form.remarks.trim()) return message("Enter request remarks", "warning");
    if (!proof) return message("Upload proof", "warning");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("proof", proof);
      const uploaded = await axios.post(
        `${base}/api/fee-approvals/${clientId}/${collegeId}/proof`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      await axios.post(`${base}/api/fee-approvals/${clientId}/${collegeId}`, {
        student_id: student.student_id,
        fee_book_id: selected.fee_book_id,
        payable_date: form.payable_date,
        fine_amount: Number(form.fine_amount || 0),
        remarks: form.remarks.trim(),
        approval_proof_path: uploaded.data?.data?.path,
        created_by: userEmail,
      });
      message("Fee Approval request saved as Draft");
      setSelected(null);
      setProof(null);
      await search();
    } catch (e) {
      message(e.response?.data?.message || "Unable to save request", "error");
    } finally {
      setBusy(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: "fee_head_name", headerName: "Fee Book / Fee Head", minWidth: 260, flex: 1 },
      {
        field: "fee_amount",
        headerName: "Total",
        width: 120,
        valueFormatter: ({ value }) => money(value),
      },
      {
        field: "balance",
        headerName: "Balance",
        width: 120,
        valueFormatter: ({ value }) => money(value),
      },
      {
        field: "approval_status",
        headerName: "Approval Status",
        width: 145,
        valueGetter: ({ row }) =>
          requests.find((x) => String(x.fee_book_id) === String(row.fee_book_id))
            ?.workflow_status || "NOT REQUESTED",
      },
      {
        field: "action",
        headerName: "Request for Approval",
        width: 190,
        sortable: false,
        renderCell: ({ row }) => {
          const active = requests.some(
            (x) =>
              String(x.fee_book_id) === String(row.fee_book_id) &&
              ["DRAFT", "APPROVED"].includes(x.workflow_status)
          );
          return (
            <Button
              variant="contained"
              size="medium"
              disabled={row.balance <= 0 || active}
              onClick={() => {
                setSelected(row);
                setForm({ payable_date: "", fine_amount: "0", remarks: "" });
                setProof(null);
              }}
              sx={{ color: "#fff" }}
            >
              Request Approval
            </Button>
          );
        },
      },
    ],
    [requests]
  );

  const visibleRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          String(
            requests.find((request) => String(request.fee_book_id) === String(row.fee_book_id))
              ?.workflow_status || ""
          ).toUpperCase() !== "COMPLETED"
      ),
    [rows, requests]
  );

  return (
    <Box p={2} pb={6}>
      <Paper>
        <Box px={3} pt={2}>
          <Typography variant="h6">FEE APPROVAL REQUEST</Typography>
        </Box>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <TextField
            size="medium"
            label="Student Roll No."
            value={rollNo}
            onChange={(e) => setRollNo(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            sx={{ minWidth: { xs: "100%", sm: 380 } }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            disabled={busy}
            onClick={search}
            sx={{ color: "#fff" }}
          >
            Search
          </Button>
        </Toolbar>
        {student && (
          <Box px={3} pb={2}>
            <Typography fontWeight="bold">
              {student.student_name} — {student.student_rollno}
            </Typography>
          </Box>
        )}
        {student && (
          <DataGrid
            autoHeight
            rows={visibleRows}
            columns={columns}
            pagination
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            disableSelectionOnClick
          />
        )}
      </Paper>
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        <DialogTitle>Request Fee Approval</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography fontWeight="bold">{selected?.fee_head_name}</Typography>
              <Typography>Fee Balance: Rs. {money(selected?.balance)}</Typography>
            </Paper>
            <TextField
              required
              type="date"
              label="Payable Date"
              InputLabelProps={{ shrink: true }}
              value={form.payable_date}
              onChange={(e) => setForm({ ...form, payable_date: e.target.value })}
            />
            <TextField
              type="number"
              label="Fine Amount"
              inputProps={{ min: 0, step: 0.01 }}
              value={form.fine_amount}
              onChange={(e) => setForm({ ...form, fine_amount: e.target.value })}
            />
            <TextField
              required
              multiline
              minRows={3}
              label="Request Remarks"
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            />
            <Box>
              <Typography
                component="label"
                variant="body2"
                fontWeight={600}
                color="text.primary"
                display="block"
                mb={0.75}
              >
                Approval Proof{" "}
                <Box component="span" color="error.main">
                  *
                </Box>
              </Typography>
              <Button
                fullWidth
                component="label"
                variant="contained"
                startIcon={<UploadFileIcon />}
                sx={{ color: "#fff", "&:hover": { color: "#fff" } }}
              >
                {proof ? proof.name : "Upload Proof (Image/PDF)"}
                <input
                  hidden
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setProof(e.target.files?.[0] || null)}
                />
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSelected(null)}
            color="error"
            variant="contained"
            sx={{ color: "#fff" }}
          >
            Cancel
          </Button>
          <Button onClick={save} disabled={busy} variant="contained" sx={{ color: "#fff" }}>
            Save as Draft
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={notice.open}
        autoHideDuration={4000}
        onClose={() => setNotice({ ...notice, open: false })}
      >
        <Alert severity={notice.severity} variant="filled">
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
